<template>
  <a-range-picker
    :value="innerValue"
    :allow-clear="allowClear"
    :autocomplete="autocomplete"
    :default-value="defaultValue"
    :disabled="disabled"
    picker="month"
    @change="handleChange"
    format="YYYY-MM"
    :placeholder="innerPlaceholder"
    :size="size"
    :style="style"
  />
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';

export default defineComponent({
  name: 'MonthRangePicker',
  props: {
    is_range: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    fix_day: {
      type: Number,
      required: false,
      default: () => 1,
    },
    start_offset_day: {
      type: Number,
      required: false,
      default: () => 0,
    },
    end_offset_day: {
      type: Number,
      required: false,
      default: () => 0,
    },
    value_has_time: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    // ----------
    value: {
      type: [String, Array],
      required: false,
      default: () => null,
    },
    allowClear: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    autocomplete: {
      type: String,
      required: false,
      default: () => 'off',
    },
    defaultValue: {
      type: String,
      required: false,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    placeholder: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => ['开始月份', '结束月份'],
    },
    size: {
      type: String,
      required: false,
      default: () => 'small',
    },
    style: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const innerPlaceholder = computed(() => {
      if (props.placeholder === null || props.placeholder === undefined) {
        return ['开始月份', '结束月份'];
      } else if (typeof props.placeholder === 'string') {
        return [props.placeholder, props.placeholder];
      } else if (Array.isArray(props.placeholder) && props.placeholder.length > 0) {
        return props.placeholder;
      } else {
        return ['开始月份', '结束月份'];
      }
    });
    const innerIsRange = computed(() => {
      if (props.is_range === null || props.is_range === undefined) {
        return false;
      } else {
        return props.is_range;
      }
    });
    const innerFixDay = computed(() => {
      if (props.fix_day === null || props.fix_day === undefined) {
        return 1;
      } else {
        return props.fix_day;
      }
    });
    const innerStartOffsetDay = computed(() => {
      if (props.start_offset_day === null || props.start_offset_day === undefined) {
        return 0;
      } else {
        return props.start_offset_day;
      }
    });
    const innerEndOffsetDay = computed(() => {
      if (props.end_offset_day === null || props.end_offset_day === undefined) {
        return 0;
      } else {
        return props.end_offset_day;
      }
    });
    const innerValueHasTime = computed(() => {
      if (props.value_has_time === null || props.value_has_time === undefined) {
        return false;
      } else {
        return props.value_has_time;
      }
    });
    const innerValue = computed(() => {
      if (Array.isArray(props.value) && props.value.length == 2) {
        if (innerIsRange.value) {
          let start_date: any = props.value[0];
          if (!start_date) {
            return undefined;
          }
          start_date = dayjs(start_date as string);
          start_date = start_date.subtract(innerStartOffsetDay.value, 'day');
          let end_date: any = props.value[1];
          if (!end_date) {
            return undefined;
          }
          end_date = dayjs(end_date);
          end_date = end_date.subtract(innerEndOffsetDay.value, 'day');
          return [start_date, end_date];
        } else {
          let start_date: any = props.value[0];
          if (!start_date) {
            return undefined;
          }
          start_date = dayjs(start_date);
          let end_date: any = props.value[1];
          if (!end_date) {
            return undefined;
          }
          end_date = dayjs(end_date);
          return [start_date, end_date];
        }
      } else {
        return undefined;
      }
    });
    const handleChange = (value: dayjs.Dayjs[]) => {
      if (!Array.isArray(value) || value.length != 2) {
        emit('update:value', null);
        return;
      }
      if (innerIsRange.value) {
        let start_date = value[0]
          .startOf('month')
          .startOf('day')
          .add(innerStartOffsetDay.value, 'day');
        let end_date = value[1].endOf('month').startOf('day').add(innerEndOffsetDay.value, 'day');
        if (innerValueHasTime.value) {
          end_date = end_date.add(1, 'day');
          emit('update:value', [
            start_date.format('YYYY-MM-DD HH:mm:ss'),
            end_date.format('YYYY-MM-DD HH:mm:ss'),
          ]);
        } else {
          emit('update:value', [start_date.format('YYYY-MM-DD'), end_date.format('YYYY-MM-DD')]);
        }
      } else {
        let result = null;
        let start_date = value[0].date(innerFixDay.value).startOf('day');
        let end_date = value[1].date(innerFixDay.value).startOf('day');
        if (innerValueHasTime.value) {
          end_date = end_date.add(1, 'day');
          result = [
            start_date.format('YYYY-MM-DD HH:mm:ss'),
            end_date.format('YYYY-MM-DD HH:mm:ss'),
          ];
        } else {
          result = [start_date.format('YYYY-MM-DD'), end_date.format('YYYY-MM-DD')];
        }
        emit('update:value', result);
      }
    };
    return {
      innerValue,
      handleChange,
      innerIsRange,
      innerFixDay,
      innerStartOffsetDay,
      innerEndOffsetDay,
      innerValueHasTime,
      innerPlaceholder,
    };
  },
  components: {},
});
</script>
